import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { downloadBlob } from "@/components/roster/shiftColours";
import { Calendar } from "@/components/ui/calendar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Download, Upload, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const YEAR = new Date().getFullYear();
const CAL_CLASSES = { caption_label: "hidden", caption_dropdowns: "flex gap-1", vhidden: "sr-only",
  dropdown: "rounded-md border border-slate-200 bg-white px-1.5 py-1 text-sm", months: "flex flex-col gap-4 lg:flex-row",
  head_cell: "w-10 text-sm font-semibold text-slate-600", day: "h-10 w-10 rounded-md text-sm font-semibold text-slate-800 hover:bg-slate-100",
  day_selected: "!bg-indigo-600 !text-white" };

function useMasters() {
  const { actingOrgId } = useAuth();
  const locs = useQuery({ queryKey: ["r-locs", actingOrgId], queryFn: async () => (await api.get("/org/masters/locations")).data });
  const depts = useQuery({ queryKey: ["r-depts", actingOrgId], queryFn: async () => (await api.get("/org/masters/departments")).data });
  return { locs: locs.data || [], depts: depts.data || [] };
}

function Picks({ label, items, value, onChange, testid }) {
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <div>
      <p className="mb-1 text-sm font-semibold text-slate-800">{label} <span className="font-normal text-slate-500">({value.length ? `${value.length} selected` : "all"})</span></p>
      <div className="flex max-h-36 flex-wrap gap-x-4 gap-y-2 overflow-y-auto rounded-md border border-slate-200 p-2">
        {items.map((x) => <label key={x.id} className="flex items-center gap-2 text-sm"><Checkbox checked={value.includes(x.id)} onCheckedChange={() => toggle(x.id)} data-testid={`${testid}-${x.id}`} /> {x.name}</label>)}
        {!items.length && <span className="text-sm text-slate-500">None in Masters</span>}
      </div>
    </div>
  );
}

const scopeText = (h, locs, depts) => {
  const n = (ids, list) => ids.map((id) => list.find((x) => x.id === id)?.name || "?").join(", ");
  return `${h.location_ids?.length ? n(h.location_ids, locs) : "All locations"} · ${h.department_ids?.length ? n(h.department_ids, depts) : "All departments"}`;
};

export default function Holidays() {
  const { actingOrgId, isManagerOnly } = useAuth();
  const canEdit = !isManagerOnly;
  const qc = useQueryClient();
  const { locs, depts } = useMasters();
  const [year, setYear] = useState(YEAR);
  const [picked, setPicked] = useState([]);
  const [form, setForm] = useState({ name: "", location_ids: [], department_ids: [] });
  const [edit, setEdit] = useState(null);
  const key = ["holidays", actingOrgId, year];
  const { data: list = [] } = useQuery({ queryKey: key, queryFn: async () => (await api.get(`/holidays?year=${year}`)).data });
  const { data: nextYear = [] } = useQuery({ queryKey: ["holidays", actingOrgId, year + 1], queryFn: async () => (await api.get(`/holidays?year=${year + 1}`)).data });
  const refresh = () => qc.invalidateQueries({ queryKey: ["holidays"] });

  const add = async () => {
    try {
      const { data } = await api.post("/holidays", { ...form, dates: picked.map((d) => format(d, "yyyy-MM-dd")) });
      toast.success(`${data.created} holiday(s) added${data.skipped ? `, ${data.skipped} already existed` : ""}`);
      setPicked([]); setForm({ ...form, name: "" }); refresh();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const remove = async (h) => {
    if (!window.confirm(`Remove ${h.name} on ${format(parseISO(h.date), "dd MMM yyyy")}?`)) return;
    try { await api.delete(`/holidays/${h.id}`); toast.success("Holiday removed"); refresh(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Holidays" description="Holidays for all or selected locations and departments. Rosters apply them automatically: people get H, except the 'On holidays' headcount who keep working, taking turns." />
      {canEdit && <ImportBar onDone={refresh} />}
      {canEdit && (
      <div className="grid gap-5 xl:grid-cols-[auto_1fr]">
        <Card className="overflow-x-auto p-3" data-testid="holiday-year-calendar">
          <Calendar mode="multiple" selected={picked} onSelect={(x) => setPicked(x || [])} numberOfMonths={2} captionLayout="dropdown-buttons"
            fromYear={YEAR - 1} toYear={YEAR + 2} onMonthChange={(m) => setYear(m.getFullYear())}
            modifiers={{ holiday: [...list, ...nextYear].map((h) => parseISO(h.date)) }} modifiersClassNames={{ holiday: "bg-amber-300 text-slate-900" }}
            classNames={CAL_CLASSES} />
          <p className="px-2 pb-1 text-xs text-slate-600"><span className="mr-1 inline-block h-3 w-3 rounded bg-amber-300 align-middle" /> existing holiday · <span className="mx-1 inline-block h-3 w-3 rounded bg-indigo-600 align-middle" /> selected</p>
        </Card>
        <Card className="space-y-4 p-5" data-testid="holiday-form">
          <p className="text-base font-bold text-slate-900">{picked.length ? `${picked.length} date(s) selected: ${picked.map((d) => format(d, "dd MMM")).join(", ")}` : "Click dates on the calendar to add holidays"}</p>
          <Input placeholder="Holiday name, e.g. Diwali" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="holiday-name" />
          <Picks label="Locations" items={locs} value={form.location_ids} onChange={(v) => setForm({ ...form, location_ids: v })} testid="hol-loc" />
          <Picks label="Departments" items={depts} value={form.department_ids} onChange={(v) => setForm({ ...form, department_ids: v })} testid="hol-dept" />
          <p className="text-xs text-slate-600">Nothing ticked = everyone. If you tick both locations and departments, the holiday applies only where both match (e.g. Sales in Pune).</p>
          <Button disabled={!picked.length || !form.name.trim()} onClick={add} data-testid="holiday-add-btn"><Plus className="mr-1 h-4 w-4" /> Add holiday</Button>
        </Card>
      </div>
      )}
      {!canEdit && (
        <div className="mb-4 flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setYear(year - 1)} data-testid="holiday-prev-year">{year - 1}</Button>
          <span className="text-base font-bold">{year}</span>
          <Button variant="outline" size="sm" onClick={() => setYear(year + 1)} data-testid="holiday-next-year">{year + 1}</Button>
        </div>
      )}
      <Card className="mt-5 divide-y divide-slate-100" data-testid="holiday-list">
        <p className="px-5 py-3 text-base font-bold text-slate-900">{list.length} holiday(s) in {year}</p>
        {list.map((h) => (
          <div key={h.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3" data-testid={`holiday-item-${h.id}`}>
            <div>
              <p className="text-base font-semibold text-slate-900">{format(parseISO(h.date), "EEE, dd MMM yyyy")} · {h.name}</p>
              <p className="text-sm text-slate-600">{scopeText(h, locs, depts)}</p>
            </div>
            {canEdit && <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={() => setEdit(h)} data-testid={`holiday-edit-${h.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button size="sm" variant="ghost" className="text-rose-700" onClick={() => remove(h)} data-testid={`holiday-delete-${h.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>}
          </div>
        ))}
      </Card>
      {edit && <EditDialog h={edit} locs={locs} depts={depts} onClose={() => setEdit(null)} onDone={() => { setEdit(null); refresh(); }} />}
    </div>
  );
}

function EditDialog({ h, locs, depts, onClose, onDone }) {
  const [f, setF] = useState({ date: h.date, name: h.name, location_ids: h.location_ids || [], department_ids: h.department_ids || [] });
  const save = async () => {
    try { await api.put(`/holidays/${h.id}`, f); toast.success("Holiday updated"); onDone(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="holiday-edit-dialog">
        <DialogHeader><DialogTitle>Edit holiday</DialogTitle></DialogHeader>
        <Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="holiday-edit-date" />
        <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="holiday-edit-name" />
        <Picks label="Locations" items={locs} value={f.location_ids} onChange={(v) => setF({ ...f, location_ids: v })} testid="hol-edit-loc" />
        <Picks label="Departments" items={depts} value={f.department_ids} onChange={(v) => setF({ ...f, department_ids: v })} testid="hol-edit-dept" />
        <DialogFooter><Button onClick={save} data-testid="holiday-edit-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportBar({ onDone }) {
  const ref = useRef(null);
  const [errors, setErrors] = useState([]);
  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/holidays/import", fd);
      setErrors(data.errors);
      toast.success(`${data.created} imported${data.skipped ? `, ${data.skipped} already existed` : ""}${data.errors.length ? `, ${data.errors.length} row(s) with problems` : ""}`);
      onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    if (ref.current) ref.current.value = "";
  };
  return (
    <div className="mb-5">
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={() => downloadBlob(api, "/holidays/template", "holiday_template.xlsx")} data-testid="holiday-template-btn"><Download className="mr-1 h-4 w-4" /> Download template</Button>
        <input ref={ref} type="file" accept=".xlsx" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} data-testid="holiday-import-input" />
        <Button variant="outline" onClick={() => ref.current?.click()} data-testid="holiday-import-btn"><Upload className="mr-1 h-4 w-4" /> Import from Excel</Button>
      </div>
      <p className="mt-2 text-xs text-slate-500" data-testid="holiday-import-hint">Template columns: Start date, End date, Holiday name, Locations, Departments. For a holiday of several days in a row, fill both dates; for one day, leave End date blank.</p>
      {!!errors.length && (
        <div className="mt-3 rounded-lg bg-rose-50 p-3 text-sm text-rose-900" data-testid="holiday-import-errors">
          {errors.map((e) => <p key={e}>{e}</p>)}
        </div>
      )}
    </div>
  );
}
